import { ReactNode, ComponentPropsWithoutRef, memo } from 'react';
import DealsBanner from '_components/UI/DealsBanner';
import { ResolvedPdpDealsBanner } from '@adapters/prismic/types/pdpDealsBanner';
import ReactMarkdown, { Options as ReactMarkdownOptions } from 'react-markdown';
import breaks from 'remark-breaks';
import gfm from 'remark-gfm';

import styles from './PdpDealsBannerList.module.scss';

type MdastNode = { type: string; data?: Record<string, unknown> };

// Annotates the last paragraph node so react-markdown/rehype passes
// data-last-paragraph="true" as a prop to the rendered <p> element.
// Extracted at module scope so the plugin reference is stable across renders,
// preserving react-markdown's processor memoisation.
const markLastParagraphPlugin = () => (tree: { children: MdastNode[] }) => {
  const paragraphs = tree.children.filter((n) => n.type === 'paragraph');
  if (paragraphs.length === 0) return;
  const last = paragraphs[paragraphs.length - 1];
  last.data ??= {};
  const nodeData = last.data as { hProperties?: Record<string, string> };
  nodeData.hProperties ??= {};
  nodeData.hProperties['data-last-paragraph'] = 'true';
};

const REMARK_PLUGINS_BASE: NonNullable<ReactMarkdownOptions['remarkPlugins']> =
  [gfm, breaks];

const REMARK_PLUGINS_WITH_SUFFIX: NonNullable<
  ReactMarkdownOptions['remarkPlugins']
> = [gfm, breaks, markLastParagraphPlugin];

interface DealsBannerMarkdownProps {
  markdown?: string;
  suffix?: ReactNode;
}

// Memoized so react-markdown (unified/remark/rehype) does not re-parse the same
// markdown string on every parent re-render — e.g. on each colour/size/variant
// selection, which changes the deals-banner resolver context upstream.
const DealsBannerMarkdown = memo(function DealsBannerMarkdown({
  markdown,
  suffix,
}: DealsBannerMarkdownProps) {
  if (!markdown) return null;

  if (!suffix) {
    return (
      <ReactMarkdown
        className={styles.markdown}
        remarkPlugins={REMARK_PLUGINS_BASE}
      >
        {markdown}
      </ReactMarkdown>
    );
  }

  return (
    <ReactMarkdown
      className={styles.markdown}
      remarkPlugins={REMARK_PLUGINS_WITH_SUFFIX}
      components={{
        p: ({
          'data-last-paragraph': isLast,
          children,
          ...props
        }: ComponentPropsWithoutRef<'p'> & {
          'data-last-paragraph'?: string;
        }) => (
          <p {...props}>
            {children}
            {isLast === 'true' ? suffix : null}
          </p>
        ),
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
});

interface PdpDealsBannerListProps {
  banners: ResolvedPdpDealsBanner[];
  containerClassName: string;
}

const PdpDealsBannerList = memo(function PdpDealsBannerList({
  banners,
  containerClassName,
}: PdpDealsBannerListProps) {
  return (
    <>
      {banners.map((banner) => (
        <div className={containerClassName} key={banner.id}>
          <DealsBanner
            variant={banner.variant}
            title={banner.title}
            firstLine={
              banner.firstLineMarkdown ? (
                <DealsBannerMarkdown markdown={banner.firstLineMarkdown} />
              ) : undefined
            }
            descriptionLine={
              banner.descriptionMarkdown ? (
                <DealsBannerMarkdown markdown={banner.descriptionMarkdown} />
              ) : undefined
            }
            infoTooltipContent={
              banner.tooltipMarkdown ? (
                <DealsBannerMarkdown markdown={banner.tooltipMarkdown} />
              ) : undefined
            }
            tooltipPlacement={banner.tooltipPlacement}
            ctaLabel={banner.ctaLabel}
            ctaHref={banner.ctaHref}
          />
        </div>
      ))}
    </>
  );
});

export default PdpDealsBannerList;
